/**
 * Toast / notification primitive — Zustand-backed, dependency-free.
 *
 * Why custom: `react-hot-toast` and friends pull in 5–10kB and a portal layer
 * we don't need. The whole module is ~70 LOC and lives entirely inside the
 * existing render tree (no portals → safe-area work without surprises in
 * Capacitor WebViews).
 *
 * USAGE:
 *   const toast = useToast();
 *   toast.success('Profile saved');
 *   toast.error('Could not save');
 *
 *   // Mount once near the App root:
 *   <ToastViewport />
 */
import { useEffect } from 'react';
import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  /** Created timestamp — used to drive auto-dismiss timers. */
  createdAt: number;
}

interface ToastState {
  items: ToastItem[];
  push: (variant: ToastVariant, message: string) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

let nextId = 1;

const useToastStore = create<ToastState>((set) => ({
  items: [],
  push: (variant, message) => {
    const id = nextId++;
    set((s) => ({
      items: [
        ...s.items,
        { id, variant, message, createdAt: Date.now() },
      ],
    }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
  clear: () => set({ items: [] }),
}));

/**
 * Stable accessor object — defined once outside React so consumers can call
 * `toast.success(...)` from event handlers, mutation callbacks, even module
 * scope, without re-creating function references on every render.
 */
const toastApi = {
  success: (message: string) => useToastStore.getState().push('success', message),
  error: (message: string) => useToastStore.getState().push('error', message),
  info: (message: string) => useToastStore.getState().push('info', message),
  dismiss: (id: number) => useToastStore.getState().dismiss(id),
  clear: () => useToastStore.getState().clear(),
};

export function useToast(): typeof toastApi {
  return toastApi;
}

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success:
    'bg-brand-success text-white border-brand-success-600',
  error:
    'bg-brand-danger text-white border-brand-danger-600',
  info:
    'bg-brand-info text-white border-brand-info-600',
};

const VARIANT_ICON: Record<ToastVariant, string> = {
  success: '✓',
  error: '!',
  info: 'i',
};

const AUTO_DISMISS_MS = 4000;

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}

function Toast({ toast, onDismiss }: ToastProps) {
  useEffect(() => {
    const handle = window.setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [toast.id, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto flex items-start gap-3 rounded-card border px-4 py-3 shadow-menu ${VARIANT_CLASSES[toast.variant]}`}
    >
      <span
        aria-hidden
        className="flex h-6 w-6 flex-none items-center justify-center rounded-pill bg-white/20 text-sm font-bold"
      >
        {VARIANT_ICON[toast.variant]}
      </span>
      <p className="flex-1 text-sm font-medium leading-snug">{toast.message}</p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="flex h-6 w-6 flex-none items-center justify-center rounded-pill text-white/80 transition-colors hover:bg-white/20 hover:text-white"
      >
        ×
      </button>
    </div>
  );
}

export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  if (items.length === 0) return null;

  return (
    <div
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4 sm:inset-x-auto sm:right-4 sm:items-end pb-safe"
    >
      {items.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}
